'use client';

export const dynamic = 'force-dynamic';

import { Button, Container, Typography, Box } from '@mui/material';
import { useSession, signOut } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useEffect, useState } from 'react';

export default function Home() {
  const { data: session, status } = useSession();
  const router = useRouter();
  
  // Add mounted state to prevent SSR issues
  const [mounted, setMounted] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string>('');

  // Add mounted effect
  useEffect(() => {
    setMounted(true);
  }, []);

  // Update useEffect to check mounted state
  useEffect(() => {
    if (!mounted) return;
    
    // Redirect super admins to their dashboard
    // Regular admins can stay on home page and access their profile
    if (session?.user?.isSuperAdmin) {
      router.push('/superadmin');
      return;
    }
    
    const fetchLastUpdated = async () => {
      try {
        const response = await fetch('/api/system/status');
        const data = await response.json();
        if (data.lastUpdated) {
          const date = new Date(data.lastUpdated);
          const stamp = `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
          setLastUpdated(data.commit?.hash ? `${stamp} (${data.commit.hash})` : stamp);
        }
      } catch (error) {
        console.error('Failed to fetch system status:', error);
        setLastUpdated('Status unavailable');
      }
    };

    fetchLastUpdated();
  }, [mounted, session, router]);

  // Early return for server-side rendering
  if (!mounted) {
    return null;
  }

  // Handle loading state
  if (status === 'loading') {
    return (
      <Container maxWidth="sm">
        <Box sx={{ my: 4, textAlign: 'center' }}>
          <Typography>Loading...</Typography>
        </Box>
      </Container>
    );
  }

  const handleSignOut = async () => {
    await signOut({ redirect: false });
    router.push('/auth/signin');
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ my: 4, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Welcome to Workforce App
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Last updated: {lastUpdated || 'Loading...'}
        </Typography>
        
        {session ? (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body1" gutterBottom>
              Signed in as {session.user?.email}
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center' }}>
              {!session.user?.isSuperAdmin && (
                <Button
                  component={Link}
                  href="/profile"
                  variant="contained"
                  color="primary"
                >
                  View Profile
                </Button>
              )}
              {session.user?.isAdmin && (
                <Button
                  component={Link}
                  href="/admin"
                  variant="contained"
                  color="secondary"
                >
                  Admin Dashboard
                </Button>
              )}

              <Button
                onClick={handleSignOut}
                variant="outlined"
                color="primary"
              >
                Sign Out
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              component={Link}
              href="/auth/signin"
              variant="contained"
              color="primary"
            >
              Sign In
            </Button>
            <Button
              component={Link}
              href="/auth/signup"
              variant="outlined"
              color="primary"
            >
              Sign Up
            </Button>
          </Box>
        )}
      </Box>
    </Container>
  );
}
